import { Injectable, inject } from "@angular/core";
import { State, Store, Selector, Action, StateContext } from "@ngxs/store";
import { SetShowLoader, ShowToastrAlert } from "app/modules/shared-state/shared.actions";
import { OnSetSharedGridRowData } from "app/modules/shared/ag-grid/shared-grid-state/shared-grid.actions";
import { catchError, tap } from "rxjs";
import { StockStateModel, StockItem, StockJournal } from "./stock.model";
import { GetAllInternalGrades, OnCreateStockJournal, OnGetStock, OnGetStockJournals, OnRemoveStockAdjustment, OnSetSelectedStock, OnThrowCantPerformActionOnGroupedStockItems } from "./stock.actions";
import { StockService } from "../stock.service";
import { SharedGridState } from "app/modules/shared/ag-grid/shared-grid-state/shared-grid.state";
import { FuseConfirmationService } from "@fuse/services/confirmation";
import { produce } from "immer";

@State<StockStateModel>({
    name: 'stockstate',
    defaults: {
        stockItems: [],
        isLoading: false,
        error: null,
        selectedStockItem: null,
        stockJournals: [],
        internalGrades: [],
        stockJournalForm: {
            model: {
                journalReference: null,
                shipmentReference: null,
                stockReference: null,
                pcNumber: null,
                internalGrade: null,
                externalGrade: null,
                noOfCartons: null,
                cartonNettWeight: null,
                quantityKgs: null,
                cartonsShippedOut: null,
                cartonsBooked: null,
                reason: null,
            },
            dirty: false,
            status: null,
            errors: null,
        }
    }
})
@Injectable()
export class StockState {

    private _stockService = inject(StockService);
    private _store = inject(Store);
    private _fuseConfirmationService = inject(FuseConfirmationService);

    @Selector()
    static isLoading(state: StockStateModel) {
        return state.isLoading;
    }

    @Selector()
    static getStockItems(state: StockStateModel) {
        return state.stockItems;
    }

    @Action(OnGetStock)
    OnGetStock(ctx: StateContext<StockStateModel>, { payload }: OnGetStock) {
        ctx.dispatch(new SetShowLoader(true));

        return this._stockService.getStockItems().pipe(
            catchError((error) => {

                ctx.dispatch(new ShowToastrAlert({
                    showToast: true,
                    toastrType: 'error',
                    messageTitle: 'Error',
                    messageBody: `Failed to load stock items: ${error.message}`,
                    position: 'toast-top-center'
                }));

                ctx.dispatch(new SetShowLoader(false));
                throw error;
            }),
            tap((stockItems: StockItem[]) => {
                ctx.dispatch(new SetShowLoader(false));
                ctx.patchState({ stockItems, });

                ctx.dispatch(new OnSetSharedGridRowData({
                    reload: true,
                    payload: stockItems,
                    gridUUID: payload.gridUUID
                }));
            })
        );
    }

    @Selector()
    static getStockJournals(state: StockStateModel) {
        return state.stockJournals;
    }

    @Action(OnGetStockJournals)
    OnGetStockJournals(ctx: StateContext<StockStateModel>) {
        ctx.dispatch(new SetShowLoader(true));

        return this._stockService.getStockJournals().pipe(
            catchError((error) => {
                ctx.dispatch(new ShowToastrAlert({
                    showToast: true,
                    toastrType: 'error',
                    messageTitle: 'Error',
                    messageBody: `Failed to load stock journals: ${error.message}`,
                    position: 'toast-top-center'
                }));
                ctx.dispatch(new SetShowLoader(false));
                throw error;
            }),
            tap((stockJournals: StockJournal[]) => {
                ctx.dispatch(new SetShowLoader(false));
                ctx.patchState({ stockJournals, });

                ctx.dispatch(new OnSetSharedGridRowData({
                    reload: true,
                    payload: stockJournals,
                    gridUUID: this._store.selectSnapshot(SharedGridState.getGridReferenceUUIDs).firstGridUUID
                }));
            })
        );
    }

    @Action(OnCreateStockJournal)
    OnCreateStockJournal(ctx: StateContext<StockStateModel>, { payload }: OnCreateStockJournal) {
        ctx.dispatch(new SetShowLoader(true));
        return this._stockService.createStockJournal(payload).pipe(
            catchError((error) => {
                ctx.dispatch(new ShowToastrAlert({
                    showToast: true,
                    toastrType: 'error',
                    messageTitle: 'Error',
                    messageBody: `Failed to load stock journals: ${error.message}`,
                    position: 'toast-top-center'
                }));
                ctx.dispatch(new SetShowLoader(false));
                this._store.dispatch(new OnGetStock({ gridUUID: this._store.selectSnapshot(SharedGridState.getGridReferenceUUIDs).firstGridUUID }));
                throw error;
            }),
            tap(() => {
                ctx.dispatch(new SetShowLoader(false));
                ctx.dispatch(new OnGetStock({ gridUUID: this._store.selectSnapshot(SharedGridState.getGridReferenceUUIDs).firstGridUUID }));
                ctx.dispatch(new ShowToastrAlert({
                    showToast: true,
                    toastrType: 'success',
                    messageTitle: 'Success',
                    messageBody: 'Stock Journal added successfully!',
                    position: 'toast-top-center'
                }));
            }),

        );
    }


    @Action(OnRemoveStockAdjustment)
    OnDeleteStockJournal(ctx: StateContext<StockStateModel>, { id }: OnRemoveStockAdjustment) {
        ctx.dispatch(new SetShowLoader(true));

        return this._stockService.deleteStockJournal(id).pipe(
            catchError((error) => {
                this._store.dispatch(new OnGetStockJournals());
                ctx.dispatch(new ShowToastrAlert({
                    showToast: true,
                    toastrType: 'error',
                    messageTitle: 'Error',
                    messageBody: `Failed to load stock journals: ${error.message}`,
                    position: 'toast-top-center'
                }));
                ctx.dispatch(new SetShowLoader(false));
                throw error;
            }),
            tap(() => {
                const state = ctx.getState();
                ctx.patchState({
                    stockJournals: state.stockJournals.filter(item => item._id !== id),
                });
                ctx.dispatch(new ShowToastrAlert({
                    showToast: true,
                    toastrType: 'success',
                    messageTitle: 'Success',
                    messageBody: 'Stock Journal deleted successfully!',
                    position: 'toast-top-center'
                }));
                ctx.dispatch(new SetShowLoader(false));
            }),
        );
    }

    @Selector()
    static getSelectedStockItem(state: StockStateModel) {
        return state.selectedStockItem;
    }

    @Action(OnSetSelectedStock)
    OnSetSelectedStock(ctx: StateContext<StockStateModel>, { payload }: OnSetSelectedStock) {
        if (!payload) {
            return this._store.dispatch(new OnThrowCantPerformActionOnGroupedStockItems());
        }
        if (payload.type === 'SJA' || payload.type === 'SSI') {
            ctx.dispatch(new ShowToastrAlert({
                showToast: true,
                toastrType: 'error',
                messageTitle: 'Error',
                messageBody: `You are unable to select SSI and SJA documents for adjustment.`,
                position: 'toast-top-center'
            }));
            throw new Error('You are unable to select SSI and SJA documents for adjustment.');
        }

        return this._stockService.onValidateStock(payload.purchaseConfirmationId, payload._id).pipe(
            catchError((error) => {
                const dialogRef = this._fuseConfirmationService.open({
                    title: 'Cannot Perform Action',
                    message: `<br> <strong class="text-red-500">${error.error.message.message}</strong>`,
                    icon: { show: true, color: 'warn' },
                    actions: {
                        confirm: {
                            color: 'warn',
                            show: false
                        }
                    },
                    dismissible: false
                });
                dialogRef.afterClosed().subscribe(async (result) => {
                    if (result === 'confirmed') {

                        return;
                    }
                });
                ctx.dispatch(new ShowToastrAlert({
                    showToast: true,
                    toastrType: 'error',
                    messageTitle: 'Error',
                    messageBody: `${error.error.message.message}`,
                    position: 'toast-top-center'
                }));
                throw error;
            }),
            tap((response) => {
                if (response.success) {
                    const updatedPayload = produce(payload, (draft) => {
                        draft.journalReference = response.nextJournalReference;
                        draft.noOfCartons = -payload.noOfCartons;
                    });
                    ctx.patchState({ selectedStockItem: updatedPayload });
                }
            })
        );
    }


    @Action(GetAllInternalGrades)
    getAllInternalGrades(ctx: StateContext<StockStateModel>) {
        return this._stockService.getInternalGrades().pipe(
            tap((internalGrades) => {
                ctx.patchState({ internalGrades });
            })
        );
    }

    @Selector()
    static getInternalGrades(state: StockStateModel): string[] {
        return state.internalGrades;
    }

    @Action(OnThrowCantPerformActionOnGroupedStockItems)
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    onThrowCantPerformActionOnGroupedStockItems(ctx: StateContext<StockStateModel>) {
        this._store.dispatch(new ShowToastrAlert({
            showToast: true,
            toastrType: 'error',
            messageTitle: 'Error',
            messageBody: `Cannot perform action on grouped stock items.`,
            position: 'toast-top-center'
        }));
        throw new Error('Cannot perform action on grouped stock items.');
    }

}
